<template>
	<page>
		<div ref="kanban" class="bake-task-kanban" :class="{fullscreen: isFullscreen}">
			<div class="kanban-header">
				<div class="header-title">
					<svg class="title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
						<path d="M8 6h13"></path>
						<path d="M8 12h13"></path>
						<path d="M8 18h13"></path>
						<path d="M3 6h.01"></path>
						<path d="M3 12h.01"></path>
						<path d="M3 18h.01"></path>
					</svg>
					<span>烘烤任务看板</span>
				</div>
				<div class="kanban-stats">
					<div class="stat-item">
						<span class="stat-value">{{ stats.total }}</span>
						<span class="stat-label">进行中<span v-if="stats.total > 50" class="stat-hint">仅展示最近50条</span></span>
					</div>
					<div class="stat-item stat-met">
						<span class="stat-value">{{ stats.met_count }}</span>
						<span class="stat-label">已达标待确认</span>
					</div>
					<div class="stat-item stat-warning">
						<span class="stat-value">{{ stats.warning_count }}</span>
						<span class="stat-label">温度预警</span>
					</div>
					<div class="stat-item stat-failed">
						<span class="stat-value">{{ stats.failed_count }}</span>
						<span class="stat-label">下发失败</span>
					</div>
				</div>
				<div class="header-tools">
					<span v-if="lastRefreshTime" class="refresh-time">
						<svg class="refresh-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
							<polyline points="23 4 23 10 17 10"></polyline>
							<path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
						</svg>
						{{lastRefreshTime}}
					</span>
					<div class="range-selector">
						<span class="range-label">曲线范围</span>
						<select v-model="chartRange" class="range-select" @change="onChartRangeChange">
							<option v-for="opt in rangeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
						</select>
					</div>
					<div class="range-selector">
						<span class="range-label">轮播</span>
						<select v-model.number="carouselInterval" class="range-select" @change="onCarouselChange">
							<option v-for="opt in carouselOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
						</select>
					</div>
					<div class="tool-actions">
						<div class="tool-btn" :class="{loading: loading}" title="立即刷新" @click="manualRefresh">
							<svg class="tool-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
								<polyline points="23 4 23 10 17 10"></polyline>
								<path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
							</svg>
						</div>
						<div class="tool-btn" :title="isFullscreen ? '退出全屏' : '全屏'" @click="isFullscreen ? exitHandle() : toggleFullscreen()">
							<svg v-if="!isFullscreen" class="tool-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
								<path d="M8 3H5a2 2 0 0 0-2 2v3"></path>
								<path d="M21 8V5a2 2 0 0 0-2-2h-3"></path>
								<path d="M3 16v3a2 2 0 0 0 2 2h3"></path>
								<path d="M16 21h3a2 2 0 0 0 2-2v-3"></path>
							</svg>
							<svg v-else class="tool-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
								<path d="M4 14h4v4"></path>
								<path d="M20 10h-4V6"></path>
								<path d="M16 10l5-5"></path>
								<path d="M8 14l-5 5"></path>
							</svg>
						</div>
					</div>
				</div>
			</div>

			<div v-if="loading && list.length == 0" class="kanban-empty">
				<div class="loading-ring"></div>
				<p>正在加载烘烤任务数据...</p>
			</div>
			<div v-else-if="list.length == 0" class="kanban-empty">
				<p>当前暂无正在烘烤的任务</p>
			</div>
			<div v-else :key="'screen_' + page + '_' + screenIndex" class="task-grid" :class="{'carousel-anim': carouselActive}" @mouseenter="carouselPaused = true" @mouseleave="carouselPaused = false">
				<div
					v-for="item in displayList"
					:key="item.id"
					class="task-card"
					:class="cardClass(item)"
				>
					<div class="progress-bar">
						<div class="progress-fill" :class="progressFillClass(item)" :style="{width: getBakeProgress(item) + '%'}"></div>
					</div>
					<div class="card-main">
						<div class="card-header">
							<div class="header-left">
								<span class="equipment-point">
									{{ item.equipment_code || '-' }}
									<span class="point-split"></span>
									{{ item.point_code != null ? item.point_code : '-' }}
								</span>
							</div>
							<div class="header-status">
								<c-state v-if="item.sync_status != 1" class="sync-status" dict="bakeSyncStatusDic" :value="item.sync_status != null ? item.sync_status : 0"></c-state>
								<span v-else class="task-status" :class="statusClass(item)">{{ stateText(item) }}</span>
							</div>
						</div>
						<div v-if="item.sync_status == 2 && item.sync_message" class="sync-fail-message">{{ item.sync_message }}</div>

						<div class="sub-info">
							<span class="work-code" @click.stop="openWorkDetail(item)">{{ item.work_code || '-' }}</span>
							<span class="product-text" :title="productText(item)">{{ productText(item) }}</span>
						</div>

						<div class="metrics">
							<div class="metric">
								<div class="metric-label">当前温度</div>
								<div class="metric-value">
									{{ item.latest_temp != null ? parseFloat(item.latest_temp).toFixed(1) + '℃' : '-' }}
								</div>
							</div>
							<div class="metric">
								<div class="metric-label">目标温度</div>
								<div class="metric-value">
									{{ item.target_temp != null ? parseFloat(item.target_temp).toFixed(1) + '℃' : '-' }}
									<small>±{{ item.temp_tolerance || 0 }}</small>
								</div>
							</div>
							<div class="metric">
								<div class="metric-label">烘烤进度</div>
								<div class="metric-value" :class="{'warming-text': item.is_warming_up == 1}">
									{{ progressText(item) }}
								</div>
							</div>
							<div class="metric">
								<div class="metric-label">已烘烤</div>
								<div class="metric-value">{{ formatBakeDuration(item) }}</div>
							</div>
							<div class="metric">
								<div class="metric-label">剩余时长</div>
								<div class="metric-value" :class="{'warming-text': item.is_warming_up == 1}">
									{{ formatRemainingTime(item) }}
								</div>
							</div>
						</div>
					</div>

					<div v-if="item.sync_status == 1" class="chart-wrapper" @click="openChartDialog(item)">
						<div :ref="'chart_' + item.id" class="task-chart"></div>
						<div class="chart-zoom-hint">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
								<circle cx="11" cy="11" r="8"></circle>
								<line x1="21" y1="21" x2="16.65" y2="16.65"></line>
								<line x1="11" y1="8" x2="11" y2="14"></line>
								<line x1="8" y1="11" x2="14" y2="11"></line>
							</svg>
							<span>点击放大</span>
						</div>
					</div>

					<div v-if="(item.sync_status == 2) || (item.is_duration_met == 1 && auth('b_bake_task_complete'))" class="action-row">
						<span v-if="item.is_duration_met == 1" class="action-hint">烘烤完成待确认</span>
						<span v-if="item.sync_status == 2" class="action-btn retry-btn" @click.stop="confirmRetry(item)">重试下发</span>
						<span v-if="item.sync_status == 2 && auth('b_bake_task_cancel')" class="action-btn cancel-btn" @click.stop="confirmCancelFailed(item)">取消</span>
						<span v-if="item.is_duration_met == 1 && auth('b_bake_task_complete')" class="action-btn confirm-btn" @click.stop="confirmComplete(item)">确认完成</span>
					</div>
				</div>
			</div>

			<div v-if="carouselActive && localScreens > 1" class="carousel-indicator" :class="{visible: showIndicator}" @mouseenter="carouselPaused = true" @mouseleave="carouselPaused = false">
				<span class="page-btn" @click="prevScreen">‹ 上一屏</span>
				<span class="page-info">{{screenIndex + 1}} / {{localScreens}} 屏 · 共 {{list.length}} 条</span>
				<span class="page-btn" @click="nextScreen">下一屏 ›</span>
			</div>
			<div v-else-if="!carouselActive && total > pageSize" class="kanban-pagination">
				<span class="page-btn" :class="{disabled: page <= 1}" @click="page > 1 && changePage(page - 1)">‹ 上一页</span>
				<span class="page-info">{{page}} / {{totalPages}} 页 · 共 {{total}} 条</span>
				<span class="page-btn" :class="{disabled: page >= totalPages}" @click="page < totalPages && changePage(page + 1)">下一页 ›</span>
			</div>
		</div>

		<c-dialog ref="chartDialog" class="chart-dialog-dark" title="烘烤温度曲线" width="1200px" height="500px" :button="false" @close="closeDialogChart">
			<div class="dialog-chart-info">
				<span class="dialog-info-item">工单：{{ dialogItem.work_code || '-' }}</span>
				<span class="dialog-info-item">机台：{{ dialogItem.equipment_code || '-' }}</span>
				<span class="dialog-info-item">点位：{{ dialogItem.point_code != null ? dialogItem.point_code : '-' }}</span>
				<span class="dialog-info-item">产品：{{ dialogItem.product_code || '-' }}</span>
				<span class="dialog-info-item">目标温度：{{ dialogItem.target_temp || '-' }}℃</span>
			</div>
			<div ref="dialogChart" class="dialog-chart"></div>
		</c-dialog>
	</page>
</template>

<script>
import * as echarts from 'echarts'

export default {
	name: 'bake_task_kanban',

	data() {
		return {
			list: [],
			loading: false,
			lastRefreshTime: '',
			isFullscreen: false,
			chartInstances: {},
			chartData: {},
			timer: null,
			chartTimer: null,
			observer: null,
			page: 1,
			pageSize: 20,
			total: 0,
			carouselInterval: 0,
			carouselOptions: [
				{ label: '关闭', value: 0 },
				{ label: '10秒/屏', value: 10 },
				{ label: '15秒/屏', value: 15 },
				{ label: '30秒/屏', value: 30 }
			],
			carouselTimer: null,
			carouselPaused: false,
			carouselAutoStarted: false,
			screenIndex: 0,
			cardsPerScreen: 8,
			showIndicator: false,
			dialogItem: {},
			dialogChartInstance: null,
			dialogChartData: null,
			chartRange: '1h',
			rangeOptions: [
				{ label: '最近1小时', value: '1h' },
				{ label: '最近6小时', value: '6h' },
				{ label: '最近12小时', value: '12h' },
				{ label: '最近24小时', value: '24h' },
				{ label: '最近3天', value: '3d' },
				{ label: '最近5天', value: '5d' },
				{ label: '最近10天', value: '10d' },
				{ label: '全部', value: 'all' }
			],
			stats: {
				total: 0,
				failed_count: 0,
				warning_count: 0,
				met_count: 0,
				running_count: 0
			}
		}
	},

	computed: {
		totalPages() {
			return Math.ceil(this.total / this.pageSize)
		},

		carouselActive() {
			return this.carouselInterval > 0
		},

		// 轮播时只渲染当前屏的卡片，减少 DOM 和图表实例
		displayList() {
			if (!this.carouselActive) return this.list
			let start = this.screenIndex * this.cardsPerScreen
			return this.list.slice(start, start + this.cardsPerScreen)
		},

		// 当前数据的总屏数（轮播模式下为全量数据，纯前端分屏）
		localScreens() {
			return Math.max(1, Math.ceil(this.list.length / this.cardsPerScreen))
		}
	},

	mounted() {
		this.loadData()
		window.addEventListener('resize', this.resizeCharts)
		this.handleFullscreenChange = () => {
			this.isFullscreen = !!document.fullscreenElement
			if (this.isFullscreen) {
				// 进入全屏自动开启轮播（默认15秒/屏），退出时恢复
				if (!this.carouselActive) {
					this.carouselAutoStarted = true
					this.carouselInterval = 15
					this.onCarouselChange()
				} else {
					this.$nextTick(() => this.computeCardsPerScreen())
				}
			} else {
				if (this.carouselAutoStarted) {
					this.carouselAutoStarted = false
					this.carouselInterval = 0
					this.onCarouselChange()
				} else if (this.carouselActive) {
					this.$nextTick(() => this.computeCardsPerScreen())
				}
			}
		}
		document.addEventListener('fullscreenchange', this.handleFullscreenChange)
		// 轮播模式下，鼠标移到屏幕底部时才浮出屏号指示器
		this.handleIndicatorMove = (e) => {
			if (!this.carouselActive) return
			this.showIndicator = e.clientY >= window.innerHeight - 80
		}
		document.addEventListener('mousemove', this.handleIndicatorMove)
		this.fullHandle()
	},

	beforeDestroy() {
		this.clearTimer()
		this.clearChartTimers()
		this.stopCarousel()
		if (this.observer) {
			this.observer.disconnect()
			this.observer = null
		}
		window.removeEventListener('resize', this.resizeCharts)
		if (this.handleFullscreenChange) {
			document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
		}
		if (this.handleIndicatorMove) {
			document.removeEventListener('mousemove', this.handleIndicatorMove)
		}
		Object.values(this.chartInstances).forEach(chart => chart && chart.dispose())
		this.chartInstances = {}
		if (this.dialogChartInstance) {
			this.dialogChartInstance.dispose()
			this.dialogChartInstance = null
		}
	},

	methods: {
		manualRefresh() {
			this.chartData = {}
			this.loadData()
		},

		onChartRangeChange() {
			this.chartData = {}
			this.cleanCharts()
			this.$nextTick(() => {
				if (this.carouselActive) {
					this.renderScreenCharts()
				} else {
					this.loadCharts()
				}
			})
		},

		toggleFullscreen() {
			if (!document.fullscreenElement) {
				document.documentElement.requestFullscreen()
			}
		},

		exitHandle() {
			if (document.exitFullscreen) {
				document.exitFullscreen();
			} else if (document.mozCancelFullScreen) {
				document.mozCancelFullScreen();
			} else if (document.webkitExitFullscreen) {
				document.webkitExitFullscreen();
			} else if (document.msExitFullscreen) {
				document.msExitFullscreen();
			}
			this.navigateTo('/');
		},

		fullHandle() {
			const elem = document.documentElement;
			if (elem.requestFullscreen) {
				elem.requestFullscreen();
			} else if (elem.mozRequestFullScreen) {
				elem.mozRequestFullScreen();
			} else if (elem.webkitRequestFullscreen) {
				elem.webkitRequestFullscreen();
			} else if (elem.msRequestFullscreen) {
				elem.msRequestFullscreen();
			}
		},

		changePage(p) {
			this.page = p
			this.screenIndex = 0
			this.loadData()
		},

		onCarouselChange() {
			this.screenIndex = 0
			this.stopCarousel()
			this.page = 1
			// 轮播开关切换时重新拉数据：轮播拉全量，非轮播按页拉
			this.loadData()
			if (this.carouselActive) {
				this.startCarousel()
			}
		},

		// 按实际渲染的卡片尺寸计算一屏能放多少张，避免轮播时铺不满或出现滚动
		computeCardsPerScreen() {
			let container = this.$refs.kanban
			if (!container) return
			// 列数：直接读 grid 实际分列结果，与 CSS auto-fill 保持一致
			let cols
			let grid = container.querySelector('.task-grid')
			if (grid) {
				cols = getComputedStyle(grid).gridTemplateColumns.split(' ').length
			} else {
				cols = Math.floor((container.clientWidth - 40 + 20) / 480)
			}
			cols = Math.max(1, cols)
			// 行高：测量实际卡片高度（含曲线区），无卡片时用经验值
			let cardEl = container.querySelector('.task-card')
			let cardH = cardEl ? cardEl.offsetHeight : 320
			let headerEl = container.querySelector('.kanban-header')
			let headerH = headerEl ? headerEl.offsetHeight + 20 : 90
			let viewH = window.innerHeight - (this.isFullscreen ? 0 : 54)
			// 指示器浮层不占布局高度，只减头部和容器上下内边距
			let availH = viewH - headerH - 40
			let rows = Math.max(1, Math.floor((availH + 20) / (cardH + 20)))
			this.cardsPerScreen = cols * rows
		},

		startCarousel() {
			this.stopCarousel()
			if (!this.carouselActive) return
			this.carouselTimer = setInterval(() => {
				if (this.carouselPaused || this.loading) return
				this.nextScreen()
			}, this.carouselInterval * 1000)
		},

		stopCarousel() {
			if (this.carouselTimer) {
				clearInterval(this.carouselTimer)
				this.carouselTimer = null
			}
		},

		// 轮播为纯前端分屏，屏间切换不发请求，避免闪屏
		nextScreen() {
			if (this.localScreens <= 1) return
			this.screenIndex = (this.screenIndex + 1) % this.localScreens
			this.$nextTick(() => this.renderScreenCharts())
		},

		prevScreen() {
			if (this.localScreens <= 1) return
			this.screenIndex = (this.screenIndex - 1 + this.localScreens) % this.localScreens
			this.$nextTick(() => this.renderScreenCharts())
		},

		// 轮播模式下当前屏卡片全部可见，直接渲染，不走 IntersectionObserver
		renderScreenCharts() {
			this.displayList.forEach(item => {
				if (this.chartData[item.id]) {
					this.renderChart(item)
				} else {
					this.loadChartData(item)
				}
			})
		},

		loadData(refreshCharts = true) {
			this.loading = true
			this.loadStats()
			this.request({
				url: '/bake/task/taskKanban',
				// 后端已按优先级排序并最多返回50条
				data: { page: 1, page_size: 500 },
				success: data => {
					this.list = data.list || []
					this.total = data.total || 0
					this.lastRefreshTime = new Date().format('yyyy-MM-dd hh:mm:ss')
					this.cleanCharts()
					if (this.carouselActive) {
						this.$nextTick(() => {
							this.computeCardsPerScreen()
							if (this.screenIndex >= this.localScreens) {
								this.screenIndex = 0
							}
							this.$nextTick(() => {
								this.renderScreenCharts()
							})
						})
					} else if (refreshCharts) {
						this.$nextTick(() => {
							this.loadCharts()
						})
					}
				},
				complete: () => {
					this.loading = false
					this.resetTimer()
					this.resetChartTimers()
				}
			})
		},
		
		loadStats() {
			this.request({
				url: '/bake/task/kanbanStats',
				data: {},
				success: data => {
					this.stats = {
						total: data.total || 0,
						failed_count: data.failed_count || 0,
						warning_count: data.warning_count || 0,
						met_count: data.met_count || 0,
						running_count: data.running_count || 0
					}
				}
			})
		},
		
		loadCharts() {
			this.list.forEach(item => {
				this.observeChart(item)
			})
		},

		observeChart(item) {
			if (!this.observer) {
				this.observer = new IntersectionObserver((entries) => {
					entries.forEach(entry => {
						if (entry.isIntersecting) {
							let taskId = entry.target.dataset.taskId
							let item = this.list.find(i => i.id == taskId)
							if (item && !this.chartData[taskId]) {
								this.loadChartData(item)
							}
							this.observer.unobserve(entry.target)
						}
					})
				}, { rootMargin: '100px' })
			}
			this.$nextTick(() => {
				let dom = this.$refs['chart_' + item.id]
				if (Array.isArray(dom)) dom = dom[0]
				if (dom) {
					dom.dataset.taskId = item.id
					this.observer.observe(dom)
				}
			})
		},

		loadChartData(item) {
			this.request({
				url: '/bake/task/recordList',
				data: { bake_task_id: item.id, range: this.chartRange },
				success: data => {
					let records = data || []
					let targetTemp = parseFloat(item.target_temp) || 0
					let tolerance = parseFloat(item.temp_tolerance) || 0
					this.$set(this.chartData, item.id, {
						records,
						targetTemp,
						tolerance
					})
					this.$nextTick(() => {
						this.renderChart(item)
					})
				}
			})
		},

		renderChart(item) {
			let taskId = item.id
			let chartData = this.chartData[taskId]
			if (!chartData) return
			let refName = 'chart_' + taskId
			let dom = this.$refs[refName]
			if (Array.isArray(dom)) dom = dom[0]
			if (!dom) return

			if (this.chartInstances[taskId]) {
				this.chartInstances[taskId].dispose()
			}
			let chart = echarts.init(dom)
			this.chartInstances[taskId] = chart

			let records = chartData.records
			let times = records.map(r => this.formatDate(r.record_time, 'MM-dd hh:mm'))
			let temps = records.map(r => {
				let v = r.current_temp
				return (v === null || v === undefined || v === '') ? null : parseFloat(v)
			})
			let lower = chartData.targetTemp - chartData.tolerance
			let upper = chartData.targetTemp + chartData.tolerance

			let markAreas = []
			let inValid = false
			let startIndex = -1
			temps.forEach((temp, index) => {
				if (temp !== null && temp >= lower && temp <= upper) {
					if (!inValid) {
						inValid = true
						startIndex = index
					}
				} else {
					if (inValid) {
						markAreas.push([{ xAxis: times[startIndex] }, { xAxis: times[index - 1] }])
						inValid = false
					}
				}
			})
			if (inValid) {
				markAreas.push([{ xAxis: times[startIndex] }, { xAxis: times[times.length - 1] }])
			}

			let validTemps = temps.filter(t => t !== null)
			let maxTemp = validTemps.length ? Math.max(...validTemps) : 0

			chart.setOption({
				grid: { top: 18, right: 12, bottom: 20, left: 40 },
				tooltip: {
					trigger: 'axis',
					backgroundColor: 'rgba(15, 23, 42, 0.9)',
					borderColor: 'rgba(56, 189, 248, 0.3)',
					textStyle: { color: '#e2e8f0' },
					formatter: params => {
						let p = params[0]
						if (p.value === null || p.value === undefined) {
							return p.name + '<br/>温度：无数据'
						}
						return p.name + '<br/>温度：' + p.value + '℃'
					}
				},
				xAxis: {
					type: 'category',
					data: times,
					axisLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.3)' } },
					axisLabel: { color: '#94a3b8', fontSize: 10, interval: Math.floor(times.length / 4) }
				},
				yAxis: {
					type: 'value',
					min: 0,
					max: Math.ceil((Math.max(maxTemp, chartData.targetTemp + chartData.tolerance) + 5) / 10) * 10,
					axisLine: { show: false },
					axisLabel: { color: '#94a3b8', fontSize: 10 },
					splitLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.1)' } }
				},
				series: [{
					data: temps,
					type: 'line',
					smooth: true,
					connectNulls: false,
					symbol: 'none',
					lineStyle: { color: '#38bdf8', width: 2 },
					areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: 'rgba(56,189,248,0.25)' }, { offset: 1, color: 'rgba(56,189,248,0)' }]) },
					markLine: {
						symbol: 'none',
						data: [{
							yAxis: chartData.targetTemp,
							lineStyle: { color: '#4ade80', type: 'dashed', width: 1 },
							label: { formatter: '目标', fontSize: 9, color: '#4ade80' }
						}]
					},
					markArea: {
						itemStyle: { color: 'rgba(74,222,128,0.12)' },
						data: markAreas
					}
				}]
			})
			chart.resize()
		},

		resizeCharts() {
			Object.values(this.chartInstances).forEach(chart => chart && chart.resize())
			if (this.carouselActive) {
				let old = this.cardsPerScreen
				this.computeCardsPerScreen()
				if (old != this.cardsPerScreen) {
					this.screenIndex = 0
					this.$nextTick(() => this.renderScreenCharts())
				}
			}
		},

		cleanCharts() {
			let activeIds = this.list.map(item => item.id)
			Object.keys(this.chartInstances).forEach(key => {
				if (!activeIds.find(id => id == key)) {
					this.chartInstances[key] && this.chartInstances[key].dispose()
					this.$delete(this.chartInstances, key)
					this.$delete(this.chartData, key)
				}
			})
			if (this.observer) {
				this.observer.disconnect()
				this.observer = null
			}
		},

		resetTimer() {
			this.clearTimer()
			this.timer = setInterval(() => {
				this.loadData(false)
			}, 180000)
		},

		clearTimer() {
			if (this.timer) {
				clearInterval(this.timer)
				this.timer = null
			}
		},

		resetChartTimers() {
			this.clearChartTimers()
			let list = this.list.filter(item => item.id)
			let len = list.length
			if (len == 0) return
			let interval = Math.floor(180000 / len)
			let index = 0
			this.chartTimer = setInterval(() => {
				let currentList = this.list.filter(item => item.id)
				if (currentList.length == 0) return
				let item = currentList[index % currentList.length]
				if (item) this.loadChartData(item)
				index = (index + 1) % currentList.length
			}, interval)
		},

		clearChartTimers() {
			if (this.chartTimer) {
				clearInterval(this.chartTimer)
				this.chartTimer = null
			}
		},

		formatDate(date, fmt) {
			if (!date) return ''
			return new Date(date).format(fmt || 'yyyy-MM-dd hh:mm')
		},

		formatBakeDuration(item) {
			if (item.is_warming_up == 1) return '升温中'
			let minutes = parseInt(item.effective_bake_minutes) || 0
			if (minutes <= 0) return '0分钟'
			if (minutes < 60) return minutes + '分钟'
			let hours = Math.floor(minutes / 60)
			let left = minutes % 60
			return left > 0 ? hours + '小时' + left + '分' : hours + '小时'
		},

		formatRemainingTime(item) {
			if (item.is_warming_up == 1) return '升温中'
			if (item.is_duration_met == 1) return '0分钟'
			let target = parseInt(item.target_duration) || 0
			let current = parseInt(item.effective_bake_minutes) || 0
			let remain = target - current
			if (remain <= 0) return '0分钟'
			if (remain < 60) return remain + '分钟'
			let hours = Math.floor(remain / 60)
			let left = remain % 60
			return left > 0 ? hours + '小时' + left + '分' : hours + '小时'
		},

		getBakeProgress(item) {
			if (item.is_warming_up == 1) return 0
			let target = parseInt(item.target_duration) || 0
			let current = parseInt(item.effective_bake_minutes) || 0
			if (target <= 0) return 0
			let percent = Math.round((current / target) * 100)
			return Math.min(100, percent)
		},

		stateText(item) {
			if (item.warning_state == 1) return '温度预警'
			let state = parseInt(item.bake_state)
			switch (state) {
				case 2:
					if (item.is_duration_met == 1) return '待确认'
					return item.is_warming_up == 1 ? '升温中' : '烘烤中'
				case 3: return '已完成'
				case 4: return '已取消'
				default: return '未知'
			}
		},

		statusClass(item) {
			if (item.warning_state == 1) return 'status-warning'
			let state = parseInt(item.bake_state)
			if (state == 2 && item.is_duration_met == 1) return 'status-met'
			if (item.is_warming_up == 1) return 'status-warming'
			if (state == 2) return 'status-running'
			if (state == 3) return 'status-done'
			if (state == 4) return 'status-cancel'
			return 'status-running'
		},

		cardClass(item) {
			let cls = {}
			if (item.warning_state == 1) cls['task-warning'] = true
			if (item.is_warming_up == 1) cls['task-warming'] = true
			if (item.is_duration_met == 1) cls['task-duration-met'] = true
			return cls
		},

		progressFillClass(item) {
			let state = parseInt(item.bake_state)
			if (item.warning_state == 1) return 'fill-warning'
			if (state == 2 && item.is_duration_met == 1) return 'fill-met'
			if (item.is_warming_up == 1) return 'fill-warming'
			if (state == 3) return 'fill-done'
			if (state == 4) return 'fill-cancel'
			return 'fill-running'
		},

		progressText(item) {
			if (item.warning_state == 1) return '温度预警'
			if (item.is_warming_up == 1) return '升温中'
			if (item.is_duration_met == 1) return '待确认'
			return this.getBakeProgress(item) + '%'
		},

		productText(item) {
			if (!item.product_code) return '-'
			return `${item.product_code} | ${item.product_name || ''} | ${item.product_spec || ''}`
		},

		openWorkDetail(item) {
			if (!item.work_id) return
			this.globalDialog('workDetail', {props: {id: item.work_id}})
		},

		confirmComplete(item) {
			if (!item.id) return
			this.$confirm({
				message: '确认该烘烤任务已完成？确认后烘烤任务将变更为已完成状态。',
				buttonName: '确定',
				cancelButton: true,
				resolve: () => {
					this.request({
						url: '/bake/task/confirmComplete',
						data: { id: item.id },
						success: () => {
							this.$message({
								type: 'success',
								message: '确认成功',
							});
							this.loadData()
						}
					})
				}
			})
		},

		confirmRetry(item) {
			if (!item.id) return
			this.$confirm({
				message: '确认重新下发该烘烤任务？系统将重新调用三方接口下发任务参数。',
				buttonName: '确定',
				cancelButton: true,
				resolve: () => {
					this.request({
						url: '/bake/task/retrySend',
						data: { id: item.id },
						loading: true,
						success: () => {
							this.$message({
								type: 'success',
								message: '重试下发成功，等待三方确认',
							});
							this.loadData()
						}
					})
				}
			})
		},

		confirmCancelFailed(item) {
			if (!item.id) return
			this.$confirm({
				message: '确认取消该下发失败的烘烤任务？取消后将物理删除该任务及相关数据，不可恢复。',
				buttonName: '确定',
				cancelButton: true,
				resolve: () => {
					this.request({
						url: '/bake/task/cancelFailed',
						data: { id: item.id },
						loading: true,
						success: () => {
							this.$message({
								type: 'success',
								message: '取消成功',
							});
							this.loadData()
						}
					})
				}
			})
		},

		openChartDialog(item) {
			if (!item.id) return
			this.dialogItem = item
			this.loadDialogChartData(item, 'all')
		},

		loadDialogChartData(item, range) {
			this.dialogChartData = null
			this.request({
				url: '/bake/task/recordList',
				data: { bake_task_id: item.id, range: range },
				success: data => {
					let records = data || []
					let targetTemp = parseFloat(item.target_temp) || 0
					let tolerance = parseFloat(item.temp_tolerance) || 0
					this.dialogChartData = { records, targetTemp, tolerance }
					this.$refs.chartDialog.open()
					this.$nextTick(() => {
						setTimeout(() => {
							this.doRenderDialogChart()
						}, 300)
					})
				}
			})
		},

		doRenderDialogChart() {
			let dom = this.$refs.dialogChart
			if (!dom || !this.dialogChartData) return
			if (this.dialogChartInstance) {
				this.dialogChartInstance.dispose()
			}
			this.dialogChartInstance = echarts.init(dom)
			let cd = this.dialogChartData
			let records = cd.records
			let times = records.map(r => this.formatDate(r.record_time, 'MM-dd hh:mm'))
			let temps = records.map(r => {
				let v = r.current_temp
				return (v === null || v === undefined || v === '') ? null : parseFloat(v)
			})
			let lower = cd.targetTemp - cd.tolerance
			let upper = cd.targetTemp + cd.tolerance
			let markAreas = []
			let inValid = false
			let startIndex = -1
			temps.forEach((temp, index) => {
				if (temp !== null && temp >= lower && temp <= upper) {
					if (!inValid) {
						inValid = true
						startIndex = index
					}
				} else {
					if (inValid) {
						markAreas.push([{ xAxis: times[startIndex] }, { xAxis: times[index - 1] }])
						inValid = false
					}
				}
			})
			if (inValid) {
				markAreas.push([{ xAxis: times[startIndex] }, { xAxis: times[times.length - 1] }])
			}

			let validTemps = temps.filter(t => t !== null)
			let maxTemp = validTemps.length ? Math.max(...validTemps) : 0

			this.dialogChartInstance.setOption({
				grid: { top: 30, right: 30, bottom: 40, left: 60 },
				tooltip: {
					trigger: 'axis',
					backgroundColor: 'rgba(15, 23, 42, 0.9)',
					borderColor: 'rgba(56, 189, 248, 0.3)',
					textStyle: { color: '#e2e8f0' },
					formatter: params => {
						let p = params[0]
						if (p.value === null || p.value === undefined) {
							return p.name + '<br/>温度：无数据'
						}
						return p.name + '<br/>温度：' + p.value + '℃'
					}
				},
				xAxis: {
					type: 'category',
					data: times,
					axisLine: { lineStyle: { color: '#94a3b8' } },
					axisLabel: { color: '#94a3b8', fontSize: 11, interval: Math.max(1, Math.floor(times.length / 8)) }
				},
				yAxis: {
					type: 'value',
					name: '温度(℃)',
					nameTextStyle: { color: '#94a3b8' },
					min: 0,
					max: Math.ceil((Math.max(maxTemp, cd.targetTemp + cd.tolerance) + 5) / 10) * 10,
					axisLine: { show: false },
					axisLabel: { color: '#94a3b8', fontSize: 11 },
					splitLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.15)' } }
				},
				series: [{
					data: temps,
					type: 'line',
					smooth: true,
					connectNulls: false,
					symbol: 'none',
					lineStyle: { color: '#38bdf8', width: 2 },
					areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: 'rgba(56,189,248,0.25)' }, { offset: 1, color: 'rgba(56,189,248,0)' }]) },
					markLine: {
						symbol: 'none',
						data: [{
							yAxis: cd.targetTemp,
							lineStyle: { color: '#4ade80', type: 'dashed', width: 1 },
							label: { formatter: '目标' + cd.targetTemp + '℃', fontSize: 10, color: '#4ade80' }
						}]
					},
					markArea: {
						itemStyle: { color: 'rgba(74,222,128,0.12)' },
						data: markAreas
					}
				}]
			})
			this.dialogChartInstance.resize()
		},

		closeDialogChart() {
			if (this.dialogChartInstance) {
				this.dialogChartInstance.dispose()
				this.dialogChartInstance = null
			}
			this.dialogChartData = null
		}
	}
}
</script>

<style lang="stylus" scoped>
.bake-task-kanban
	min-height calc(100vh - 54px)
	padding 20px
	background linear-gradient(135deg, #0b1220 0%, #111827 50%, #0f172a 100%)
	box-sizing border-box

	&.fullscreen
		position fixed
		top 0
		left 0
		right 0
		bottom 0
		z-index 100
		padding 20px
		overflow auto
		background #0b1220

.kanban-header
	display flex
	justify-content space-between
	align-items center
	gap 16px
	margin-bottom 20px
	padding 14px 20px
	background rgba(255, 255, 255, 0.04)
	border 1px solid rgba(56, 189, 248, 0.12)
	border-radius 12px
	box-shadow 0 4px 20px rgba(0, 0, 0, 0.2)

.kanban-stats
	display flex
	align-items center
	gap 24px
	flex 1
	justify-content center

.stat-item
	display flex
	align-items baseline
	gap 6px

.stat-value
	font-size 22px
	font-weight 700
	color #f8fafc

.stat-label
	font-size 13px
	color #94a3b8

.stat-hint
	font-size 11px
	color #64748b
	margin-left 4px

.stat-met .stat-value
	color #22c55e

.stat-warning .stat-value
	color #f59e0b

.stat-failed .stat-value
	color #ef4444

.header-title
	display flex
	align-items center
	gap 12px
	font-size 22px
	font-weight 700
	color #f8fafc

.title-icon
	width 28px
	height 28px
	color #38bdf8

.header-tools
	display flex
	align-items center
	gap 14px

.refresh-time
	display inline-flex
	align-items center
	gap 6px
	font-size 13px
	color #94a3b8
	white-space nowrap

.refresh-icon
	width 14px
	height 14px

.tool-actions
	display flex
	align-items center
	gap 8px

.tool-btn
	display inline-flex
	align-items center
	justify-content center
	width 32px
	height 32px
	border-radius 8px
	background rgba(30, 41, 59, 0.6)
	border 1px solid rgba(148, 163, 184, 0.15)
	color #94a3b8
	cursor pointer
	transition all 0.2s ease

	&:hover
		background rgba(56, 189, 248, 0.12)
		color #38bdf8
		border-color rgba(56, 189, 248, 0.3)

	&.loading .tool-icon
		animation spin 1s linear infinite

.tool-icon
	width 16px
	height 16px

.kanban-empty
	display flex
	flex-direction column
	align-items center
	justify-content center
	padding 120px 0
	color #94a3b8
	font-size 15px

.loading-ring
	width 40px
	height 40px
	border 3px solid rgba(56, 189, 248, 0.2)
	border-top-color #38bdf8
	border-radius 50%
	animation spin 1s linear infinite
	margin-bottom 16px

@keyframes spin
	0%
		transform rotate(0deg)
	100%
		transform rotate(360deg)

.task-grid
	display grid
	grid-template-columns repeat(auto-fill, minmax(460px, 1fr))
	gap 20px

	&.carousel-anim
		animation screenFade 0.5s ease

@keyframes screenFade
	0%
		opacity 0
		transform translateY(10px)
	100%
		opacity 1
		transform translateY(0)

.task-card
	position relative
	overflow hidden
	background rgba(255, 255, 255, 0.04)
	border 1px solid rgba(148, 163, 184, 0.12)
	border-radius 12px
	transition all 0.3s ease

	&:hover
		transform translateY(-2px)
		box-shadow 0 8px 24px rgba(0, 0, 0, 0.25)

.task-card:before
	display none

.task-duration-met
	background rgba(74, 222, 128, 0.06) !important

.task-warning
	background rgba(239, 68, 68, 0.08) !important
	border-color rgba(239, 68, 68, 0.5) !important
	box-shadow 0 0 20px rgba(239, 68, 68, 0.15) !important
	animation warningPulse 2s infinite

@keyframes warningPulse
	0%
		box-shadow 0 0 20px rgba(239, 68, 68, 0.15)
	50%
		box-shadow 0 0 30px rgba(239, 68, 68, 0.3)
	100%
		box-shadow 0 0 20px rgba(239, 68, 68, 0.15)

@keyframes metPulse
	0%
		box-shadow 0 0 0 0 rgba(74, 222, 128, 0.4)
	70%
		box-shadow 0 0 0 12px rgba(74, 222, 128, 0)
	100%
		box-shadow 0 0 0 0 rgba(74, 222, 128, 0)

.progress-bar
	width 100%
	height 3px
	background rgba(148, 163, 184, 0.1)
	overflow hidden

.progress-fill
	height 100%
	transition width 0.4s ease

.fill-running
	background linear-gradient(90deg, #38bdf8, #0ea5e9)

.fill-warming
	background linear-gradient(90deg, #fbbf24, #f59e0b)

.fill-met
	background linear-gradient(90deg, #4ade80, #22c55e)

.fill-done
	background linear-gradient(90deg, #4ade80, #22c55e)

.fill-cancel
	background linear-gradient(90deg, #f87171, #ef4444)

.fill-warning
	background linear-gradient(90deg, #f87171, #ef4444)

.card-main
	padding 16px 18px 12px

.card-header
	display flex
	justify-content space-between
	align-items center
	margin-bottom 10px

.header-left
	display flex
	align-items center
	gap 10px

.equipment-point
	display inline-flex
	align-items center
	gap 6px
	padding 5px 12px
	border-radius 10px
	background rgba(56, 189, 248, 0.12)
	border 1px solid rgba(56, 189, 248, 0.25)
	font-size 18px
	font-weight 800
	color #f8fafc

.point-split
	width 4px
	height 4px
	border-radius 50%
	background rgba(248, 250, 252, 0.6)

.task-status
	padding 4px 12px
	border-radius 12px
	font-size 13px
	font-weight 700

.header-status
	display flex
	align-items center

.sync-status
	transform scale(0.85)
	transform-origin right center

.sync-fail-message
	margin-top -6px
	margin-bottom 10px
	padding 6px 10px
	border-radius 6px
	font-size 12px
	color #f87171
	background rgba(248, 113, 113, 0.12)
	line-height 1.4
	word-break break-all

.sub-info
	display flex
	align-items center
	gap 10px
	margin-bottom 10px
	padding 8px 10px
	background rgba(15, 23, 42, 0.35)
	border 1px solid rgba(148, 163, 184, 0.08)
	border-radius 8px
	font-size 12px

.work-code
	flex-shrink 0
	font-size 13px
	font-weight 600
	color #38bdf8
	cursor pointer

	&:hover
		text-decoration underline

.product-text
	flex 1
	color #94a3b8
	white-space nowrap
	overflow hidden
	text-overflow ellipsis

.status-wait
	color #94a3b8
	background rgba(148, 163, 184, 0.15)

.status-running
	color #38bdf8
	background rgba(56, 189, 248, 0.12)

.status-warming
	color #fbbf24
	background rgba(251, 191, 36, 0.12)

.status-done
	color #4ade80
	background rgba(74, 222, 128, 0.12)

.status-met
	color #4ade80
	background rgba(74, 222, 128, 0.15)
	font-weight 700

.status-warning
	color #ef4444
	background rgba(239, 68, 68, 0.15)
	font-weight 700

.status-cancel
	color #f87171
	background rgba(248, 113, 113, 0.12)

.info-row
	display flex
	align-items flex-start
	margin-bottom 8px
	font-size 13px
	line-height 1.5

.info-label
	width 40px
	flex-shrink 0
	color #64748b

.info-value
	flex 1
	color #e2e8f0

.metrics
	display flex
	justify-content space-between
	margin-top 14px
	padding-top 14px
	border-top 1px solid rgba(148, 163, 184, 0.1)

.metric
	text-align center
	flex 1

.metric-label
	font-size 11px
	color #64748b
	margin-bottom 5px

.metric-value
	font-size 15px
	font-weight 700
	color #f8fafc

	&.warming-text
		color #fbbf24

.metric-value small
	font-size 10px
	font-weight normal
	color #94a3b8
	margin-left 3px

.action-row
	display flex
	justify-content space-between
	align-items center
	margin-top 14px
	padding 14px 18px
	border-top 1px solid rgba(148, 163, 184, 0.1)
	background rgba(15, 23, 42, 0.25)
	gap 12px

.action-hint
	font-size 13px
	font-weight 600
	color #4ade80

.action-btn
	font-size 13px
	font-weight 600
	padding 6px 14px
	border-radius 4px
	cursor pointer

.confirm-btn
	color #0f172a
	background #4ade80
	box-shadow 0 0 0 0 rgba(74, 222, 128, 0.5)
	animation metPulse 2s infinite

	&:hover
		background #22c55e

.retry-btn
	color #0f172a
	background #fbbf24

	&:hover
		background #f59e0b

.cancel-btn
	color #0f172a
	background #f87171

	&:hover
		background #ef4444

.task-chart
	width 100%
	height 120px
	border-top 1px solid rgba(148, 163, 184, 0.08)

.chart-wrapper
	position relative
	cursor pointer

	&:hover .chart-zoom-hint
		opacity 1

.chart-zoom-hint
	position absolute
	top 10px
	right 10px
	display flex
	align-items center
	gap 4px
	padding 4px 10px
	background rgba(15, 23, 42, 0.8)
	border 1px solid rgba(56, 189, 248, 0.3)
	border-radius 6px
	font-size 11px
	color #38bdf8
	opacity 0
	transition opacity 0.2s

	svg
		width 14px
		height 14px

.dialog-chart-info
	display flex
	flex-wrap wrap
	gap 20px
	padding 12px 16px
	background rgba(56, 189, 248, 0.08)
	border-bottom 1px solid rgba(56, 189, 248, 0.12)

.dialog-info-item
	font-size 13px
	color #e2e8f0

.dialog-range-item
	display flex
	align-items center

.range-selector
	display flex
	align-items center
	margin-right 8px
	padding 4px 8px
	background rgba(15, 23, 42, 0.6)
	border 1px solid rgba(56, 189, 248, 0.2)
	border-radius 4px
	white-space nowrap

.range-label
	font-size 13px
	color #94a3b8
	margin-right 6px

.range-select
	padding 4px 8px
	font-size 13px
	color #e2e8f0
	background rgba(15, 23, 42, 0.8)
	border 1px solid rgba(56, 189, 248, 0.3)
	border-radius 4px
	cursor pointer
	outline none

	&:hover
		border-color rgba(56, 189, 248, 0.6)

.dialog-chart
	width 100%
	height 400px
	background #0f172a

.chart-dialog-dark
	/deep/ .c-dialog-wrapper
		background transparent

	/deep/ .c-dialog
		background #0f172a
		border 1px solid rgba(56, 189, 248, 0.2)
		box-shadow 0 20px 60px rgba(0, 0, 0, 0.6)
		overflow hidden

	/deep/ .c-dialog-head
		background #0f172a
		border-bottom 1px solid rgba(56, 189, 248, 0.15)
		color #f8fafc

	/deep/ .c-dialog-handle-item
		color #94a3b8

		&:hover
			color #38bdf8

.kanban-pagination
	display flex
	justify-content center
	align-items center
	gap 20px
	margin-top 20px
	padding 10px 0

.carousel-indicator
	position fixed
	left 50%
	bottom 16px
	z-index 120
	display flex
	align-items center
	gap 20px
	padding 10px 24px
	background rgba(15, 23, 42, 0.92)
	border 1px solid rgba(56, 189, 248, 0.25)
	border-radius 24px
	box-shadow 0 8px 32px rgba(0, 0, 0, 0.5)
	opacity 0
	transform translate(-50%, 16px)
	pointer-events none
	transition opacity 0.25s ease, transform 0.25s ease

	&.visible
		opacity 1
		transform translate(-50%, 0)
		pointer-events auto

.page-btn
	font-size 13px
	font-weight 600
	color #38bdf8
	cursor pointer
	user-select none
	white-space nowrap
	transition color 0.2s

	&:hover
		color #7dd3fc

	&.disabled
		color #475569
		cursor not-allowed

.page-info
	font-size 13px
	color #94a3b8
	white-space nowrap
</style>
